import {
  createSelector,
  createStructuredSelector,
  type Selector,
} from 'reselect';

import {type Ref as CustomRunColorsRef} from '../../../../state/views/customRunColors/types';
import {type Ref as PanelRef} from '../../../../state/views/panel/types';
import {type Ref as PanelBankConfigRef} from '../../../../state/views/panelBankConfig/types';
import {
  makeSectionMediaActualSizeSelector,
  makeSectionMediaAlwaysOriginalSelector,
  makeSectionMediaBoundingBoxConfigSelector,
  makeSectionMediaColumnCountSelector,
  makeSectionMediaCompareSettingsSelector,
  makeSectionMediaDisableVideoStreamSelector,
  makeSectionMediaFitToDimensionSelector,
  makeSectionMediaGallerySettingsSelector,
  makeSectionMediaGridSettingsSelector,
  makeSectionMediaMaxGalleryItemsSelector,
  makeSectionMediaMaxRunsSelector,
  makeSectionMediaMaxYAxisCountSelector,
  makeSectionMediaModeSelector,
  makeSectionMediaObject3DBackgroundColorSelector,
  makeSectionMediaPixelatedSelector,
  makeSectionMediaPointColorSelector,
  makeSectionMediaSegmentationMaskConfigSelector,
  makeSectionMediaSliderKeySelector,
  makeSectionMediaSliderSyncSelector,
  makeSectionMediaSnapToExistingStepSelector,
  makeSectionMediaStepStrideLengthSelector,
  makeSectionMediaUseRightHandedSystemSelector,
  makeSectionMediaVideoActualSizeSelector,
  makeSectionMediaVideoPlaybackSyncAutoplaySelector,
  makeSectionMediaVideoPlaybackSyncLoopSelector,
  makeSectionMediaVideoPlaybackSyncSelector,
  makeSectionMediaVideoPlayerTimeStepSelector,
} from '../../../../state/views/panelBankSectionConfig/selectors';
import {
  type PanelBankSectionConfigNormalized,
  type Ref as SectionRef,
} from '../../../../state/views/panelBankSectionConfig/types';
import {
  makeWorkspaceMediaActualSizeSelector,
  makeWorkspaceMediaAlwaysOriginalSelector,
  makeWorkspaceMediaBoundingBoxConfigSelector,
  makeWorkspaceMediaColumnCountSelector,
  makeWorkspaceMediaCompareSettingsSelector,
  makeWorkspaceMediaDisableVideoStreamSelector,
  makeWorkspaceMediaFitToDimensionSelector,
  makeWorkspaceMediaGallerySettingsSelector,
  makeWorkspaceMediaGridSettingsSelector,
  makeWorkspaceMediaMaxGalleryItemsSelector,
  makeWorkspaceMediaMaxRunsSelector,
  makeWorkspaceMediaMaxYAxisCountSelector,
  makeWorkspaceMediaModeSelector,
  makeWorkspaceMediaObject3DBackgroundColorSelector,
  makeWorkspaceMediaPixelatedSelector,
  makeWorkspaceMediaPointColorSelector,
  makeWorkspaceMediaSegmentationMaskConfigSelector,
  makeWorkspaceMediaSliderKeySelector,
  makeWorkspaceMediaSliderSyncSelector,
  makeWorkspaceMediaSnapToExistingStepSelector,
  makeWorkspaceMediaStepStrideLengthSelector,
  makeWorkspaceMediaUseRightHandedSystemSelector,
  makeWorkspaceMediaVideoActualSizeSelector,
  makeWorkspaceMediaVideoPlaybackSyncSelector,
  makeWorkspaceMediaVideoPlayerTimeStepSelector,
  makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
  makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
} from '../../../../state/views/workspaceSettings/selectors';
import {type Ref as WorkspaceSettingsRef} from '../../../../state/views/workspaceSettings/types';
import {type RootState} from '../../../../types/redux';
import {panelViews} from '../../../PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../PanelMediaBrowser';
import {type LayoutMode} from '../../../PanelMediaBrowser/common';
import {
  DEFAULT_MAX_Y_AXIS_COUNT,
  MAX_COL_COUNT,
  MIN_COL_COUNT,
} from '../../../PanelMediaBrowser/config';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from '../runLinePlots/maxRunDefaults';
import {mediaSettings} from './settingFactory/mediaSettingsTable';
import {type MediaSetting, type MediaSettingsTab, SettingsLevel} from './types';

export type MediaOverrideCounts = Partial<
  Record<keyof MediaBrowserPanelConfig, number>
>;

export type SectionWithRef = {
  ref: SectionRef;
  section: PanelBankSectionConfigNormalized;
};

export type MediaPanelConfigWithRef = {
  ref: PanelRef;
  config: MediaBrowserPanelConfig;
};

type SectionWithMediaSettings = {
  ref: SectionRef;
  sectionSettings: {media: MediaBrowserPanelConfig};
};

// declaring default values outside to avoid creating empty array each time the selector is called
const DEFAULT_SECTIONS_WITH_MEDIA_SETTINGS: SectionWithMediaSettings[] = [];
const DEFAULT_MEDIA_PANELS: MediaPanelConfigWithRef[] = [];

// =============================================================================
// Base State Slice Selectors
// =============================================================================
// Simple state accessors, no derived data so no need for createSelector

const selectPanelBankConfigSectionRefs =
  (panelBankConfigRef: PanelBankConfigRef) => (state: RootState) =>
    state.views.parts[panelBankConfigRef.type][panelBankConfigRef.id]
      .sectionRefs;

const selectPanelBankSections = (state: RootState) =>
  state.views.parts['panel-bank-section-config'];

const selectPanelParts = (state: RootState) => state.views.parts.panel;

export const selectPanel = (panelRef: PanelRef) => (state: RootState) =>
  state.views.parts[panelRef.type][panelRef.id];

export const selectCustomRunColors =
  (customRunColorsRef: CustomRunColorsRef) => (state: RootState) =>
    state.views.parts[customRunColorsRef.type][customRunColorsRef.id];

// =============================================================================
// Media Settings Selectors
// =============================================================================
// These selectors are used by MediaWorkspaceSettingsContextProvider to
// efficiently access sections and panels for override counting/clearing.
// They use createSelector for memoization, only recomputing when the
// underlying state slices change.

/**
 * Returns all sections that have media settings configured.
 * Used for counting and clearing section-level overrides.
 *
 * @param panelBankConfigRef - The ref to the panel bank config, or undefined
 * @returns Memoized selector returning sections with their media settings
 */
export const selectSectionsWithMediaSettings = (
  panelBankConfigRef: PanelBankConfigRef | undefined
): Selector<RootState, SectionWithMediaSettings[]> => {
  if (!panelBankConfigRef) {
    return () => DEFAULT_SECTIONS_WITH_MEDIA_SETTINGS;
  }

  return createSelector(
    selectPanelBankConfigSectionRefs(panelBankConfigRef),
    selectPanelBankSections,
    (sectionRefs, sections) => {
      const sectionsWithMediaSettings: SectionWithMediaSettings[] = [];
      for (const sectionRef of sectionRefs) {
        const mediaSettings =
          sections[sectionRef.id]?.sectionSettings?.media ?? {};

        // only look at sections where there are media settings fields actually set
        if (Object.keys(mediaSettings).length > 0) {
          sectionsWithMediaSettings.push({
            ref: sectionRef,
            sectionSettings: {
              media: mediaSettings,
            },
          });
        }
      }
      return sectionsWithMediaSettings;
    }
  );
};

/**
 * Selector factory that returns all media browser panels with their refs.
 * Used for workspace-level settings to track and clear panel overrides.
 *
 * @param panelBankConfigRef - The ref to the panel bank config, or undefined
 * @returns A memoized selector returning media panels with refs, or empty array if ref is undefined
 */
export const selectAllMediaPanelConfigsWithRefs = (
  panelBankConfigRef: PanelBankConfigRef | undefined
): Selector<RootState, MediaPanelConfigWithRef[]> => {
  if (!panelBankConfigRef) {
    return () => DEFAULT_MEDIA_PANELS;
  }

  return createSelector(
    selectPanelBankConfigSectionRefs(panelBankConfigRef),
    selectPanelBankSections,
    selectPanelParts,
    (sectionRefs, sections, panelParts) => {
      const mediaPanels: MediaPanelConfigWithRef[] = [];

      for (const sectionRef of sectionRefs) {
        for (const panelRef of sections[sectionRef.id]?.panelRefs ?? []) {
          const panel = panelParts[panelRef.id];
          if (panel.viewType === panelViews.mediaBrowser) {
            mediaPanels.push({
              ref: panelRef,
              config: panel.config as MediaBrowserPanelConfig,
            });
          }
        }
      }
      return mediaPanels;
    }
  );
};

/**
 * Returns all media browser panels within a specific section.
 * Used for counting and clearing panel-level overrides at the section level.
 *
 * @param sectionRef - The ref to the section
 * @returns Memoized selector returning media panels with their configs
 */
export const selectMediaPanelConfigsInSection = (
  sectionRef: SectionRef
): Selector<RootState, MediaPanelConfigWithRef[]> => {
  return createSelector(
    selectPanelBankSections,
    selectPanelParts,
    (sections, panelParts) => {
      const mediaPanels: MediaPanelConfigWithRef[] = [];
      const section = sections[sectionRef.id];

      if (!section) {
        return mediaPanels;
      }

      for (const panelRef of section.panelRefs) {
        const panel = panelParts[panelRef.id];
        if (panel.viewType === panelViews.mediaBrowser) {
          mediaPanels.push({
            ref: panelRef,
            config: panel.config as MediaBrowserPanelConfig,
          });
        }
      }
      return mediaPanels;
    }
  );
};

// sliderSync selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaSliderSyncSelector =
  mediaSettings.sliderSync.selectors.makeCascadingSectionSelector;

export const makeMediaSliderSyncDisplaySelector =
  mediaSettings.sliderSync.selectors.makeDisplaySelector;

// mediaSliderKey core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaSliderKeySelector =
  mediaSettings.mediaSliderKey.selectors.makeCascadingSectionSelector;

export const makePanelMediaSliderKeySelector =
  mediaSettings.mediaSliderKey.selectors.makePanelSelector;

export const makeCascadingPanelMediaSliderKeySelector =
  mediaSettings.mediaSliderKey.selectors.makeCascadingPanelSelector;

export const makeMediaSliderKeyDisplaySelector =
  mediaSettings.mediaSliderKey.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for SliderKey — mirrors
 * `config.mediaSliderKey != null` on config with defaults, which always
 * resolves to a key.
 */
export const makeSliderKeyShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaSliderKeyDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel
    ),
    mediaSliderKey => mediaSliderKey != null
  );
};

// mode core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaModeSelector =
  mediaSettings.mode.selectors.makeCascadingSectionSelector;

export const makePanelMediaModeSelector =
  mediaSettings.mode.selectors.makePanelSelector;

export const makeCascadingPanelMediaModeSelector =
  mediaSettings.mode.selectors.makeCascadingPanelSelector;

export const makeMediaModeDisplaySelector =
  mediaSettings.mode.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for LayoutMode — mirrors
 * `config.mode != null` on config with defaults, which always resolves to a
 * mode.
 */
export const makeLayoutModeShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode != null
  );
};

/**
 * Component remount key for LayoutMode — mirrors getComponentKey on cascaded config.
 */
export const makeLayoutModeComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => `${mode}-new-layout`
  );
};

// snapToExistingStep core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaSnapToExistingStepSelector =
  mediaSettings.snapToExistingStep.selectors.makeCascadingSectionSelector;

export const makePanelMediaSnapToExistingStepSelector =
  mediaSettings.snapToExistingStep.selectors.makePanelSelector;

export const makeCascadingPanelMediaSnapToExistingStepSelector =
  mediaSettings.snapToExistingStep.selectors.makeCascadingPanelSelector;

export const makeMediaSnapToExistingStepDisplaySelector =
  mediaSettings.snapToExistingStep.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for SnapToExistingStep — mirrors
 * `config.snapToExistingStep != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeSnapToExistingStepShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaSnapToExistingStepDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    snapToExistingStep => snapToExistingStep != null
  );
};

/**
 * Component remount key for SnapToExistingStep — mirrors getComponentKey on cascaded config.
 */
export const makeSnapToExistingStepComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaSnapToExistingStepDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    snapToExistingStep => `${snapToExistingStep.toString()}`
  );
};

// maxRuns core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaMaxRunsSelector =
  mediaSettings.maxRuns.selectors.makeCascadingSectionSelector;

export const makePanelMediaMaxRunsSelector =
  mediaSettings.maxRuns.selectors.makePanelSelector;

export const makeCascadingPanelMediaMaxRunsSelector =
  mediaSettings.maxRuns.selectors.makeCascadingPanelSelector;

export const makeMediaMaxRunsDisplaySelector =
  mediaSettings.maxRuns.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for MaxRuns — mirrors
 * `config.maxRuns != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeMaxRunsShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaMaxRunsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxRuns => maxRuns != null
  );
};

/**
 * Component remount key for MaxRuns — mirrors getComponentKey on cascaded config.
 */
export const makeMaxRunsComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaMaxRunsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxRuns => `${maxRuns}`
  );
};

// maxGalleryItems core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaMaxGalleryItemsSelector =
  mediaSettings.maxGalleryItems.selectors.makeCascadingSectionSelector;

export const makePanelMediaMaxGalleryItemsSelector =
  mediaSettings.maxGalleryItems.selectors.makePanelSelector;

export const makeCascadingPanelMediaMaxGalleryItemsSelector =
  mediaSettings.maxGalleryItems.selectors.makeCascadingPanelSelector;

export const makeMediaMaxGalleryItemsDisplaySelector =
  mediaSettings.maxGalleryItems.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for MaxItemCount — mirrors
 * `config.maxGalleryItems != null` on the cascaded config.
 */
export const makeMaxGalleryItemsShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaMaxGalleryItemsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxGalleryItems => maxGalleryItems != null
  );
};

/**
 * Component remount key for MaxItemCount — mirrors getComponentKey on cascaded config.
 */
export const makeMaxGalleryItemsComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaMaxGalleryItemsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxGalleryItems => `${maxGalleryItems?.toString()}`
  );
};

// maxYAxisCount core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaMaxYAxisCountSelector =
  mediaSettings.maxYAxisCount.selectors.makeCascadingSectionSelector;

export const makePanelMediaMaxYAxisCountSelector =
  mediaSettings.maxYAxisCount.selectors.makePanelSelector;

export const makeCascadingPanelMediaMaxYAxisCountSelector =
  mediaSettings.maxYAxisCount.selectors.makeCascadingPanelSelector;

export const makeMediaMaxYAxisCountDisplaySelector =
  mediaSettings.maxYAxisCount.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for MaxRowCount — mirrors
 * `config.maxYAxisCount != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeMaxYAxisCountShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaMaxYAxisCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxYAxisCount => maxYAxisCount != null
  );
};

/**
 * Component remount key for MaxRowCount — mirrors getComponentKey on cascaded config.
 */
export const makeMaxYAxisCountComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaMaxYAxisCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    maxYAxisCount => `${maxYAxisCount.toString()}`
  );
};

// columnCount core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaColumnCountSelector =
  mediaSettings.columnCount.selectors.makeCascadingSectionSelector;

export const makePanelMediaColumnCountSelector =
  mediaSettings.columnCount.selectors.makePanelSelector;

export const makeCascadingPanelMediaColumnCountSelector =
  mediaSettings.columnCount.selectors.makeCascadingPanelSelector;

export const makeMediaColumnCountDisplaySelector =
  mediaSettings.columnCount.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for NumberOfColumns — mirrors
 * `config.columnCount != null` on cascaded config.
 */
export const makeColumnCountShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaColumnCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    columnCount => columnCount != null
  );
};

/**
 * Component remount key for NumberOfColumns — mirrors getComponentKey on cascaded config.
 */
export const makeColumnCountComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaColumnCountDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    columnCount => `${columnCount.toString()}`
  );
};

// actualSize core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaActualSizeSelector =
  mediaSettings.actualSize.selectors.makeCascadingSectionSelector;

export const makePanelMediaActualSizeSelector =
  mediaSettings.actualSize.selectors.makePanelSelector;

export const makeCascadingPanelMediaActualSizeSelector =
  mediaSettings.actualSize.selectors.makeCascadingPanelSelector;

export const makeMediaActualSizeDisplaySelector =
  mediaSettings.actualSize.selectors.makeDisplaySelector;

// fitToDimension core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaFitToDimensionSelector =
  mediaSettings.fitToDimension.selectors.makeCascadingSectionSelector;

export const makePanelMediaFitToDimensionSelector =
  mediaSettings.fitToDimension.selectors.makePanelSelector;

export const makeCascadingPanelMediaFitToDimensionSelector =
  mediaSettings.fitToDimension.selectors.makeCascadingPanelSelector;

export const makeMediaFitToDimensionDisplaySelector =
  mediaSettings.fitToDimension.selectors.makeDisplaySelector;

// videoActualSize core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaVideoActualSizeSelector =
  mediaSettings.videoActualSize.selectors.makeCascadingSectionSelector;

export const makePanelMediaVideoActualSizeSelector =
  mediaSettings.videoActualSize.selectors.makePanelSelector;

export const makeCascadingPanelMediaVideoActualSizeSelector =
  mediaSettings.videoActualSize.selectors.makeCascadingPanelSelector;

export const makeMediaVideoActualSizeDisplaySelector =
  mediaSettings.videoActualSize.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for OriginalSize — mirrors
 * `config.videoActualSize != null` on config with defaults, which always
 * resolves to a value.
 */
export const makeOriginalSizeShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaVideoActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoActualSize => videoActualSize != null
  );
};

/**
 * Component remount key for OriginalSize — mirrors getComponentKey on cascaded
 * config.
 */
export const makeOriginalSizeComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoActualSize => `${videoActualSize.toString()}`
  );
};

// alwaysOriginal core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaAlwaysOriginalSelector =
  mediaSettings.alwaysOriginal.selectors.makeCascadingSectionSelector;

export const makePanelMediaAlwaysOriginalSelector =
  mediaSettings.alwaysOriginal.selectors.makePanelSelector;

export const makeCascadingPanelMediaAlwaysOriginalSelector =
  mediaSettings.alwaysOriginal.selectors.makeCascadingPanelSelector;

export const makeMediaAlwaysOriginalDisplaySelector =
  mediaSettings.alwaysOriginal.selectors.makeDisplaySelector;

/**
 * Component remount key for ImageFitAndResolution — mirrors getComponentKey on
 * cascaded config. The three image fit settings are mutually exclusive, so the
 * component re-mounts whenever any of them changes.
 */
export const makeImageFitAndResolutionComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaFitToDimensionDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaAlwaysOriginalDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (actualSize, fitToDimension, alwaysOriginal) =>
      `${actualSize?.toString()},${fitToDimension?.toString()},${alwaysOriginal?.toString()}`
  );
};

// gallerySettings core selectors are generated by the settings factory; these
// named exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaGallerySettingsSelector =
  mediaSettings.gallerySettings.selectors.makeCascadingSectionSelector;

export const makePanelMediaGallerySettingsSelector =
  mediaSettings.gallerySettings.selectors.makePanelSelector;

export const makeCascadingPanelMediaGallerySettingsSelector =
  mediaSettings.gallerySettings.selectors.makeCascadingPanelSelector;

export const makeMediaGallerySettingsDisplaySelector =
  mediaSettings.gallerySettings.selectors.makeDisplaySelector;

// gridSettings core selectors are generated by the settings factory; these
// named exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaGridSettingsSelector =
  mediaSettings.gridSettings.selectors.makeCascadingSectionSelector;

export const makePanelMediaGridSettingsSelector =
  mediaSettings.gridSettings.selectors.makePanelSelector;

export const makeCascadingPanelMediaGridSettingsSelector =
  mediaSettings.gridSettings.selectors.makeCascadingPanelSelector;

export const makeMediaGridSettingsDisplaySelector =
  mediaSettings.gridSettings.selectors.makeDisplaySelector;

/**
 * Component remount key for the grid axis dropdowns — mirrors getComponentKey
 * on cascaded config. The two axes are interdependent since they swap when the
 * same axis is selected for both.
 */
export const makeGridSettingsComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef,
  isSingleRun = false
): Selector<RootState, string> => {
  return createSelector(
    makeMediaGridSettingsDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef,
      isSingleRun
    ),
    gridSettings => `${gridSettings.xAxis},${gridSettings.yAxis}`
  );
};

// videoPlaybackSync core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaVideoPlaybackSyncSelector =
  mediaSettings.videoPlaybackSync.selectors.makeCascadingSectionSelector;

export const makePanelMediaVideoPlaybackSyncSelector =
  mediaSettings.videoPlaybackSync.selectors.makePanelSelector;

export const makeCascadingPanelMediaVideoPlaybackSyncSelector =
  mediaSettings.videoPlaybackSync.selectors.makeCascadingPanelSelector;

export const makeMediaVideoPlaybackSyncDisplaySelector =
  mediaSettings.videoPlaybackSync.selectors.makeDisplaySelector;

/**
 * Whether the sync toggle is locked because a higher level enabled it:
 * section is inherited from an enabled workspace value, and panel from an
 * enabled workspace or section value.
 */
export const makeMediaVideoPlaybackSyncIsInheritedSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, boolean> => {
  return createSelector(
    mediaSettings.videoPlaybackSync.selectors.makeWorkspaceSelector(
      workspaceSettingsRef
    ),
    mediaSettings.videoPlaybackSync.selectors.makeSectionSelector(sectionRef),
    (workspaceVideoPlaybackSync, sectionVideoPlaybackSync) => {
      const isEnabledAtWorkspace = workspaceVideoPlaybackSync === true;
      const isEnabledAtSection = sectionVideoPlaybackSync === true;
      switch (settingsLevel) {
        case SettingsLevel.PANEL:
          return isEnabledAtWorkspace || isEnabledAtSection;
        case SettingsLevel.SECTION:
          return isEnabledAtWorkspace;
        default:
          return false;
      }
    }
  );
};

/**
 * Component remount key for VideoPlaybackSync — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlaybackSyncComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlaybackSyncDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlaybackSync => `${videoPlaybackSync.toString()}`
  );
};

// videoPlaybackSyncAutoplay core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector =
  mediaSettings.videoPlaybackSyncAutoplay.selectors
    .makeCascadingSectionSelector;

export const makePanelMediaVideoPlaybackSyncAutoplaySelector =
  mediaSettings.videoPlaybackSyncAutoplay.selectors.makePanelSelector;

export const makeCascadingPanelMediaVideoPlaybackSyncAutoplaySelector =
  mediaSettings.videoPlaybackSyncAutoplay.selectors.makeCascadingPanelSelector;

export const makeMediaVideoPlaybackSyncAutoplayDisplaySelector =
  mediaSettings.videoPlaybackSyncAutoplay.selectors.makeDisplaySelector;

/**
 * Component remount key for VideoPlaybackSyncAutoplay — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlaybackSyncAutoplayComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlaybackSyncAutoplayDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlaybackSyncAutoplay => `${videoPlaybackSyncAutoplay.toString()}`
  );
};

// videoPlaybackSyncLoop core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaVideoPlaybackSyncLoopSelector =
  mediaSettings.videoPlaybackSyncLoop.selectors.makeCascadingSectionSelector;

export const makePanelMediaVideoPlaybackSyncLoopSelector =
  mediaSettings.videoPlaybackSyncLoop.selectors.makePanelSelector;

export const makeCascadingPanelMediaVideoPlaybackSyncLoopSelector =
  mediaSettings.videoPlaybackSyncLoop.selectors.makeCascadingPanelSelector;

export const makeMediaVideoPlaybackSyncLoopDisplaySelector =
  mediaSettings.videoPlaybackSyncLoop.selectors.makeDisplaySelector;

/**
 * Component remount key for VideoPlaybackSyncLoop — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlaybackSyncLoopComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlaybackSyncLoopDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlaybackSyncLoop => `${videoPlaybackSyncLoop.toString()}`
  );
};

// videoPlayerTimeStep core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaVideoPlayerTimeStepSelector =
  mediaSettings.videoPlayerTimeStep.selectors.makeCascadingSectionSelector;

export const makePanelMediaVideoPlayerTimeStepSelector =
  mediaSettings.videoPlayerTimeStep.selectors.makePanelSelector;

export const makeCascadingPanelMediaVideoPlayerTimeStepSelector =
  mediaSettings.videoPlayerTimeStep.selectors.makeCascadingPanelSelector;

export const makeMediaVideoPlayerTimeStepDisplaySelector =
  mediaSettings.videoPlayerTimeStep.selectors.makeDisplaySelector;

/**
 * Component remount key for VideoPlayerTimeStep — mirrors getComponentKey on cascaded config.
 */
export const makeVideoPlayerTimeStepComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaVideoPlayerTimeStepDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    videoPlayerTimeStep => `${videoPlayerTimeStep?.toString()}`
  );
};

// compareSettings core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaCompareSettingsSelector =
  mediaSettings.compareSettings.selectors.makeCascadingSectionSelector;

export const makePanelMediaCompareSettingsSelector =
  mediaSettings.compareSettings.selectors.makePanelSelector;

export const makeCascadingPanelMediaCompareSettingsSelector =
  mediaSettings.compareSettings.selectors.makeCascadingPanelSelector;

export const makeMediaCompareSettingsDisplaySelector =
  mediaSettings.compareSettings.selectors.makeDisplaySelector;

// boundingBoxConfig core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaBoundingBoxConfigSelector =
  mediaSettings.boundingBoxConfig.selectors.makeCascadingSectionSelector;

export const makePanelMediaBoundingBoxConfigSelector =
  mediaSettings.boundingBoxConfig.selectors.makePanelSelector;

export const makeCascadingPanelMediaBoundingBoxConfigSelector =
  mediaSettings.boundingBoxConfig.selectors.makeCascadingPanelSelector;

export const makeMediaBoundingBoxConfigDisplaySelector =
  mediaSettings.boundingBoxConfig.selectors.makeDisplaySelector;

// segmentationMaskConfig core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaSegmentationMaskConfigSelector =
  mediaSettings.segmentationMaskConfig.selectors.makeCascadingSectionSelector;

export const makePanelMediaSegmentationMaskConfigSelector =
  mediaSettings.segmentationMaskConfig.selectors.makePanelSelector;

export const makeCascadingPanelMediaSegmentationMaskConfigSelector =
  mediaSettings.segmentationMaskConfig.selectors.makeCascadingPanelSelector;

export const makeMediaSegmentationMaskConfigDisplaySelector =
  mediaSettings.segmentationMaskConfig.selectors.makeDisplaySelector;

/**
 * Raw panel-level chartTitle from the panel's own config.
 * chartTitle is a panel-only setting: it never cascades from workspace or section.
 */
export const makePanelMediaChartTitleSelector =
  mediaSettings.chartTitle.selectors.makePanelSelector;

/**
 * Raw panel-level mediaKeys from the panel's own config.
 * mediaKeys is a panel-only setting: it never cascades from workspace or section.
 */
export const makePanelMediaKeysSelector =
  mediaSettings.mediaKeys.selectors.makePanelSelector;

// useRightHandedSystem core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaUseRightHandedSystemSelector =
  mediaSettings.useRightHandedSystem.selectors.makeCascadingSectionSelector;

export const makePanelMediaUseRightHandedSystemSelector =
  mediaSettings.useRightHandedSystem.selectors.makePanelSelector;

export const makeCascadingPanelMediaUseRightHandedSystemSelector =
  mediaSettings.useRightHandedSystem.selectors.makeCascadingPanelSelector;

export const makeMediaUseRightHandedSystemDisplaySelector =
  mediaSettings.useRightHandedSystem.selectors.makeDisplaySelector;

/**
 * Component remount key for AxesHandedness — mirrors getComponentKey on cascaded config.
 */
export const makeUseRightHandedSystemComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaUseRightHandedSystemDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    useRightHandedSystem => `${useRightHandedSystem.toString()}`
  );
};

// pointColor core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaPointColorSelector =
  mediaSettings.pointColor.selectors.makeCascadingSectionSelector;

export const makePanelMediaPointColorSelector =
  mediaSettings.pointColor.selectors.makePanelSelector;

export const makeCascadingPanelMediaPointColorSelector =
  mediaSettings.pointColor.selectors.makeCascadingPanelSelector;

export const makeMediaPointColorDisplaySelector =
  mediaSettings.pointColor.selectors.makeDisplaySelector;

// object3DBackgroundColor core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export {OBJECT3D_BACKGROUND_COLOR_DEFAULT} from './settingFactory/mediaSettingsTable';

export const makeCascadingSectionMediaObject3DBackgroundColorSelector =
  mediaSettings.object3DBackgroundColor.selectors.makeCascadingSectionSelector;

export const makePanelMediaObject3DBackgroundColorSelector =
  mediaSettings.object3DBackgroundColor.selectors.makePanelSelector;

export const makeCascadingPanelMediaObject3DBackgroundColorSelector =
  mediaSettings.object3DBackgroundColor.selectors.makeCascadingPanelSelector;

export const makeMediaObject3DBackgroundColorDisplaySelector =
  mediaSettings.object3DBackgroundColor.selectors.makeDisplaySelector;

// disableVideoStream core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaDisableVideoStreamSelector =
  mediaSettings.disableVideoStream.selectors.makeCascadingSectionSelector;

export const makePanelMediaDisableVideoStreamSelector =
  mediaSettings.disableVideoStream.selectors.makePanelSelector;

export const makeCascadingPanelMediaDisableVideoStreamSelector =
  mediaSettings.disableVideoStream.selectors.makeCascadingPanelSelector;

export const makeMediaDisableVideoStreamDisplaySelector =
  mediaSettings.disableVideoStream.selectors.makeDisplaySelector;

// pixelated core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaPixelatedSelector =
  mediaSettings.pixelated.selectors.makeCascadingSectionSelector;

export const makePanelMediaPixelatedSelector =
  mediaSettings.pixelated.selectors.makePanelSelector;

export const makeCascadingPanelMediaPixelatedSelector =
  mediaSettings.pixelated.selectors.makeCascadingPanelSelector;

export const makeMediaPixelatedDisplaySelector =
  mediaSettings.pixelated.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for SmoothImage — mirrors
 * `config.pixelated != null` on config with defaults, which always
 * resolves to a value.
 */
export const makePixelatedShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaPixelatedDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    pixelated => pixelated != null
  );
};

/**
 * Component remount key for SmoothImage — mirrors getComponentKey on cascaded config.
 */
export const makePixelatedComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaPixelatedDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    pixelated => `${pixelated.toString()}`
  );
};

// stepStrideLength core selectors are generated by the settings factory; these named
// exports are thin wrappers so existing call sites don't change. See
// settingFactory/mediaSettingsTable.ts.
export const makeCascadingSectionMediaStepStrideLengthSelector =
  mediaSettings.stepStrideLength.selectors.makeCascadingSectionSelector;

export const makePanelMediaStepStrideLengthSelector =
  mediaSettings.stepStrideLength.selectors.makePanelSelector;

export const makeCascadingPanelMediaStepStrideLengthSelector =
  mediaSettings.stepStrideLength.selectors.makeCascadingPanelSelector;

export const makeMediaStepStrideLengthDisplaySelector =
  mediaSettings.stepStrideLength.selectors.makeDisplaySelector;

/**
 * Whether to show override clear rows for StrideLength — mirrors
 * `config.stepStrideLength != null` on cascaded config.
 */
export const makeStepStrideLengthShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  return createSelector(
    makeMediaStepStrideLengthDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    stepStrideLength => stepStrideLength != null
  );
};

/**
 * Component remount key for StrideLength — mirrors getComponentKey on cascaded config.
 */
export const makeStepStrideLengthComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string> => {
  return createSelector(
    makeMediaStepStrideLengthDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    stepStrideLength => `${stepStrideLength?.toString()}`
  );
};

/**
 * Whether to show override clear rows for SliderSync — mirrors
 * `config.sliderSync != null` on spread-cascaded media config.
 */
export const makeSliderSyncShowOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, boolean> => {
  return createSelector(
    makeWorkspaceMediaSliderSyncSelector(workspaceSettingsRef),
    makeSectionMediaSliderSyncSelector(sectionRef),
    (workspaceSliderSync, sectionSliderSync) => {
      const sliderSync =
        settingsLevel === SettingsLevel.SECTION
          ? sectionSliderSync !== undefined
            ? sectionSliderSync
            : workspaceSliderSync
          : workspaceSliderSync;
      return sliderSync != null;
    }
  );
};

/**
 * Component remount key for SliderSync — mirrors getComponentKey on cascaded config.
 */
export const makeSliderSyncComponentKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel
): Selector<RootState, string> => {
  return createSelector(
    makeMediaSliderSyncDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel
    ),
    makeMediaSliderKeyDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel
    ),
    (sliderSync, mediaSliderKey) =>
      `${sliderSync.toString()},${mediaSliderKey.toString()}`
  );
};

// =============================================================================
// Per-setting shouldShow selectors (drawer row visibility)
// =============================================================================

export const makeModeIsCompareShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode === 'compare'
  );

export const makeModeIsNotCompareShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode !== 'compare'
  );

export const makeModeIsGalleryShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode === 'gallery'
  );

export const makeModeIsGridShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    mode => mode === 'grid'
  );

/**
 * Sliders like columnCount/maxGalleryItems/maxYAxisCount are hidden when the
 * media is sized by actualSize/fitToDimension (images) or videoActualSize
 * (videos). Those settings only apply in gallery mode (see
 * `getConfigWithDefaults`), so they're treated as false in other modes.
 */
const makeSizedByFitSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaFitToDimensionDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaVideoActualSizeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (mode, actualSize, fitToDimension, videoActualSize) =>
      mode === 'gallery' && (actualSize || fitToDimension || videoActualSize)
  );

export const makeColumnCountShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeSizedByFitSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    sizedByImageSettings =>
      // There's no point in showing the slider if there's not more than one column number option
      MAX_COL_COUNT > MIN_COL_COUNT && !sizedByImageSettings
  );

export const makeMaxGalleryItemsShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeSizedByFitSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (sizedByImageSettings, mode) =>
      MAX_COL_COUNT > MIN_COL_COUNT &&
      !sizedByImageSettings &&
      mode === 'gallery'
  );

export const makeMaxYAxisCountShouldShowSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> =>
  createSelector(
    makeSizedByFitSettingsSelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    makeMediaModeDisplaySelector(
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    ),
    (sizedByImageSettings, mode) =>
      MAX_COL_COUNT > MIN_COL_COUNT && !sizedByImageSettings && mode === 'grid'
  );

const ALWAYS_VISIBLE_SELECTOR: Selector<RootState, boolean> = () => true;

/**
 * Whether any of the given settings' rows are visible. Settings without a
 * `shouldShowSelector` are always visible (static conditions are applied
 * beforehand in `getMediaSettingsTabs`).
 */
export const makeAnyMediaSettingVisibleSelector = (
  settings: MediaSetting[],
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, boolean> => {
  if (settings.length === 0) {
    return () => false;
  }
  const rowSelectors = settings.map(
    setting =>
      setting.shouldShowSelector?.(
        workspaceSettingsRef,
        sectionRef,
        settingsLevel,
        panelRef
      ) ?? ALWAYS_VISIBLE_SELECTOR
  );
  return createSelector(rowSelectors, (...rowsVisible: boolean[]) =>
    rowsVisible.some(Boolean)
  );
};

/** Titles of the tabs that have at least one visible setting row. */
export const makeVisibleMediaTabTitlesSelector = (
  tabs: MediaSettingsTab[],
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  settingsLevel: SettingsLevel,
  panelRef?: PanelRef
): Selector<RootState, string[]> => {
  if (tabs.length === 0) {
    return () => [];
  }
  const tabSelectors = tabs.map(tab =>
    makeAnyMediaSettingVisibleSelector(
      tab.sections.flatMap(section => section.settings),
      workspaceSettingsRef,
      sectionRef,
      settingsLevel,
      panelRef
    )
  );
  return createSelector(tabSelectors, (...tabsVisible: boolean[]) =>
    tabs.filter((_, i) => tabsVisible[i]).map(tab => tab.title)
  );
};

// =============================================================================
// Cascaded panel media config (panel render path)
// =============================================================================

/** Raw panel config for a Media Browser panel (undefined for other viewTypes). */
export const makePanelMediaBrowserConfigSelector = (
  panelRef: PanelRef | undefined
): Selector<RootState, MediaBrowserPanelConfig | undefined> => {
  if (panelRef == null) {
    return () => undefined;
  }
  return createSelector(selectPanel(panelRef), panel =>
    panel?.viewType === panelViews.mediaBrowser
      ? (panel.config as MediaBrowserPanelConfig)
      : undefined
  );
};

const makeCascadingRawValueSelector = <T>(
  workspaceSelector: Selector<RootState, T | undefined>,
  sectionSelector: Selector<RootState, T | undefined>,
  panelSelector: Selector<RootState, T | undefined>
): Selector<RootState, T | undefined> =>
  createSelector(
    workspaceSelector,
    sectionSelector,
    panelSelector,
    (workspaceValue, sectionValue, panelValue) =>
      panelValue ?? sectionValue ?? workspaceValue
  );

/**
 * The panel's fully cascaded (workspace → section → panel) raw media config,
 * composed from the per-field selectors. This replaces injecting merged
 * settings into the panel via `inheritedSettings.media`; defaults are still
 * applied downstream by `getConfigWithDefaults`.
 */
export const makeCascadingPanelMediaConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined,
  panelRef: PanelRef | undefined,
  enabled = true
): Selector<RootState, MediaBrowserPanelConfig> => {
  if (!enabled && panelRef != null) {
    // short circuit for non-media browser panels
    return createSelector(
      selectPanel(panelRef),
      panel => (panel?.config ?? {}) as MediaBrowserPanelConfig
    );
  }

  const cascadedFieldsSelector = createStructuredSelector<
    RootState,
    Partial<MediaBrowserPanelConfig>
  >({
    mode: makeCascadingRawValueSelector(
      makeWorkspaceMediaModeSelector(workspaceSettingsRef),
      makeSectionMediaModeSelector(sectionRef),
      makePanelMediaModeSelector(panelRef)
    ),
    mediaSliderKey: makeCascadingRawValueSelector(
      makeWorkspaceMediaSliderKeySelector(workspaceSettingsRef),
      makeSectionMediaSliderKeySelector(sectionRef),
      makePanelMediaSliderKeySelector(panelRef)
    ),
    snapToExistingStep: makeCascadingRawValueSelector(
      makeWorkspaceMediaSnapToExistingStepSelector(workspaceSettingsRef),
      makeSectionMediaSnapToExistingStepSelector(sectionRef),
      makePanelMediaSnapToExistingStepSelector(panelRef)
    ),
    stepStrideLength: makeCascadingRawValueSelector(
      makeWorkspaceMediaStepStrideLengthSelector(workspaceSettingsRef),
      makeSectionMediaStepStrideLengthSelector(sectionRef),
      makePanelMediaStepStrideLengthSelector(panelRef)
    ),
    pixelated: makeCascadingRawValueSelector(
      makeWorkspaceMediaPixelatedSelector(workspaceSettingsRef),
      makeSectionMediaPixelatedSelector(sectionRef),
      makePanelMediaPixelatedSelector(panelRef)
    ),
    columnCount: makeCascadingRawValueSelector(
      makeWorkspaceMediaColumnCountSelector(workspaceSettingsRef),
      makeSectionMediaColumnCountSelector(sectionRef),
      makePanelMediaColumnCountSelector(panelRef)
    ),
    maxRuns: makeCascadingRawValueSelector(
      makeWorkspaceMediaMaxRunsSelector(workspaceSettingsRef),
      makeSectionMediaMaxRunsSelector(sectionRef),
      makePanelMediaMaxRunsSelector(panelRef)
    ),
    maxGalleryItems: makeCascadingRawValueSelector(
      makeWorkspaceMediaMaxGalleryItemsSelector(workspaceSettingsRef),
      makeSectionMediaMaxGalleryItemsSelector(sectionRef),
      makePanelMediaMaxGalleryItemsSelector(panelRef)
    ),
    maxYAxisCount: makeCascadingRawValueSelector(
      makeWorkspaceMediaMaxYAxisCountSelector(workspaceSettingsRef),
      makeSectionMediaMaxYAxisCountSelector(sectionRef),
      makePanelMediaMaxYAxisCountSelector(panelRef)
    ),
    actualSize: makeCascadingRawValueSelector(
      makeWorkspaceMediaActualSizeSelector(workspaceSettingsRef),
      makeSectionMediaActualSizeSelector(sectionRef),
      makePanelMediaActualSizeSelector(panelRef)
    ),
    fitToDimension: makeCascadingRawValueSelector(
      makeWorkspaceMediaFitToDimensionSelector(workspaceSettingsRef),
      makeSectionMediaFitToDimensionSelector(sectionRef),
      makePanelMediaFitToDimensionSelector(panelRef)
    ),
    videoActualSize: makeCascadingRawValueSelector(
      makeWorkspaceMediaVideoActualSizeSelector(workspaceSettingsRef),
      makeSectionMediaVideoActualSizeSelector(sectionRef),
      makePanelMediaVideoActualSizeSelector(panelRef)
    ),
    alwaysOriginal: makeCascadingRawValueSelector(
      makeWorkspaceMediaAlwaysOriginalSelector(workspaceSettingsRef),
      makeSectionMediaAlwaysOriginalSelector(sectionRef),
      makePanelMediaAlwaysOriginalSelector(panelRef)
    ),
    gallerySettings: makeCascadingRawValueSelector(
      makeWorkspaceMediaGallerySettingsSelector(workspaceSettingsRef),
      makeSectionMediaGallerySettingsSelector(sectionRef),
      makePanelMediaGallerySettingsSelector(panelRef)
    ),
    gridSettings: makeCascadingRawValueSelector(
      makeWorkspaceMediaGridSettingsSelector(workspaceSettingsRef),
      makeSectionMediaGridSettingsSelector(sectionRef),
      makePanelMediaGridSettingsSelector(panelRef)
    ),
    compareSettings: makeCascadingRawValueSelector(
      makeWorkspaceMediaCompareSettingsSelector(workspaceSettingsRef),
      makeSectionMediaCompareSettingsSelector(sectionRef),
      makePanelMediaCompareSettingsSelector(panelRef)
    ),
    boundingBoxConfig: makeCascadingRawValueSelector(
      makeWorkspaceMediaBoundingBoxConfigSelector(workspaceSettingsRef),
      makeSectionMediaBoundingBoxConfigSelector(sectionRef),
      makePanelMediaBoundingBoxConfigSelector(panelRef)
    ),
    segmentationMaskConfig: makeCascadingRawValueSelector(
      makeWorkspaceMediaSegmentationMaskConfigSelector(workspaceSettingsRef),
      makeSectionMediaSegmentationMaskConfigSelector(sectionRef),
      makePanelMediaSegmentationMaskConfigSelector(panelRef)
    ),
    useRightHandedSystem: makeCascadingRawValueSelector(
      makeWorkspaceMediaUseRightHandedSystemSelector(workspaceSettingsRef),
      makeSectionMediaUseRightHandedSystemSelector(sectionRef),
      makePanelMediaUseRightHandedSystemSelector(panelRef)
    ),
    pointColor: makeCascadingRawValueSelector(
      makeWorkspaceMediaPointColorSelector(workspaceSettingsRef),
      makeSectionMediaPointColorSelector(sectionRef),
      makePanelMediaPointColorSelector(panelRef)
    ),
    object3DBackgroundColor: makeCascadingRawValueSelector(
      makeWorkspaceMediaObject3DBackgroundColorSelector(workspaceSettingsRef),
      makeSectionMediaObject3DBackgroundColorSelector(sectionRef),
      makePanelMediaObject3DBackgroundColorSelector(panelRef)
    ),
    disableVideoStream: makeCascadingRawValueSelector(
      makeWorkspaceMediaDisableVideoStreamSelector(workspaceSettingsRef),
      makeSectionMediaDisableVideoStreamSelector(sectionRef),
      makePanelMediaDisableVideoStreamSelector(panelRef)
    ),
    videoPlayerTimeStep: makeCascadingRawValueSelector(
      makeWorkspaceMediaVideoPlayerTimeStepSelector(workspaceSettingsRef),
      makeSectionMediaVideoPlayerTimeStepSelector(sectionRef),
      makePanelMediaVideoPlayerTimeStepSelector(panelRef)
    ),
    videoPlaybackSyncAutoplay: makeCascadingRawValueSelector(
      makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector(
        workspaceSettingsRef
      ),
      makeSectionMediaVideoPlaybackSyncAutoplaySelector(sectionRef),
      makePanelMediaVideoPlaybackSyncAutoplaySelector(panelRef)
    ),
    videoPlaybackSyncLoop: makeCascadingRawValueSelector(
      makeWorkspaceSettingsVideoPlaybackSyncLoopSelector(workspaceSettingsRef),
      makeSectionMediaVideoPlaybackSyncLoopSelector(sectionRef),
      makePanelMediaVideoPlaybackSyncLoopSelector(panelRef)
    ),
    // reversed override, videoPlaybackSync is inherited from the higher level
    videoPlaybackSync: createSelector(
      makeWorkspaceMediaVideoPlaybackSyncSelector(workspaceSettingsRef),
      makeSectionMediaVideoPlaybackSyncSelector(sectionRef),
      makePanelMediaVideoPlaybackSyncSelector(panelRef),
      (workspaceValue, sectionValue, panelValue) =>
        sectionValue ?? workspaceValue ?? panelValue
    ),
    sliderSync: makeCascadingRawValueSelector(
      makeWorkspaceMediaSliderSyncSelector(workspaceSettingsRef),
      makeSectionMediaSliderSyncSelector(sectionRef),
      () => undefined // sliderSync is not settable at the panel level
    ),
  });

  return createSelector(
    makePanelMediaBrowserConfigSelector(panelRef),
    cascadedFieldsSelector,
    (panelConfig, cascadedFields): MediaBrowserPanelConfig => ({
      // panel-only keys (chartTitle, mediaKeys, stepIndex, selection, etc.)
      ...panelConfig,
      ...cascadedFields,
    })
  );
};
